import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Trash, Brain } from "@phosphor-icons/react";

const STYLES = [["short", "Кратко"], ["normal", "Обычный"], ["detailed", "Подробно"]];
const MODELS = [["gpt", "GPT-5.4"], ["claude", "Claude Sonnet 5"]];

export const AISettingsDialog = ({ open, onOpenChange, onSaved }) => {
  const [s, setS] = useState(null);
  const [memory, setMemory] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/ai/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/ai/memory").then((r) => setMemory(r.data.memory)).catch(() => {});
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/ai/settings", s);
      toast.success("Настройки бота сохранены");
      onSaved?.(s);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const delFact = async (id) => {
    setMemory((m) => m.filter((x) => x.id !== id));
    try { await api.delete(`/agent/memory/${id}`); } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Настройки чат-бота</DialogTitle></DialogHeader>
        {s && (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground">Стиль ответов</label>
              <div className="flex gap-1.5 mt-1">
                {STYLES.map(([id, label]) => (
                  <button key={id} onClick={() => setS({ ...s, style: id })} data-testid={`ai-style-${id}`}
                    className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors ${s.style === id ? "bg-[#0084ff] text-white" : "bg-white/[0.05] text-muted-foreground"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Модель</label>
              <div className="flex gap-1.5 mt-1">
                {MODELS.map(([id, label]) => (
                  <button key={id} onClick={() => setS({ ...s, model: id })} data-testid={`ai-model-${id}`}
                    className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors ${s.model === id ? "bg-[#0084ff] text-white" : "bg-white/[0.05] text-muted-foreground"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Поиск в интернете</p>
                <p className="text-[11px] text-muted-foreground">Бот ищет свежую информацию и отвечает со ссылками</p>
              </div>
              <Switch checked={s.web_search} onCheckedChange={(v) => setS({ ...s, web_search: v })} data-testid="ai-websearch-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Авто-озвучка ответов</p>
                <p className="text-[11px] text-muted-foreground">Бот читает каждый ответ голосом</p>
              </div>
              <Switch checked={s.auto_voice} onCheckedChange={(v) => setS({ ...s, auto_voice: v })} data-testid="ai-autovoice-switch" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Свои инструкции боту</label>
              <Textarea value={s.instructions} onChange={(e) => setS({ ...s, instructions: e.target.value })}
                placeholder="Например: обращайся ко мне на «ты», я вегетарианец, не советуй бег из-за колена…"
                maxLength={500} rows={3} data-testid="ai-instructions-input" className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground flex items-center gap-1"><Brain size={13} /> Память о вас ({memory.length}/50)</label>
              <div className="mt-1 max-h-36 overflow-y-auto space-y-1" data-testid="ai-memory-list">
                {memory.length === 0 && <p className="text-xs text-muted-foreground py-1">Пока пусто — скажите боту «запомни, что…»</p>}
                {memory.map((m) => (
                  <div key={m.id} className="flex items-start gap-2 text-xs bg-white/[0.04] rounded-md px-2.5 py-1.5" data-testid="ai-memory-item">
                    <span className="flex-1">{m.fact}</span>
                    <button onClick={() => delFact(m.id)} data-testid={`ai-memory-delete-${m.id}`} className="text-muted-foreground hover:text-red-400 shrink-0"><Trash size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={save} disabled={busy} className="w-full" data-testid="ai-settings-save">Сохранить</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
